import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GraduationCap,
  Search,
  Info,
  AlertTriangle,
  Users,
  BookOpen,
  Clock,
  Calendar,
  MapPin,
  CheckCircle2,
} from 'lucide-react';
import { ProfessorTurmaPainel } from './ProfessorTurmaPainel';
import type { TurmaDetalheData } from './TurmaDetalheSheet';
import {
  buildGradeTurma,
  TURNO_LABEL,
  type useMinhasTurmasProfessor,
} from './useMinhasTurmasProfessor';
import { useAuthStore } from '../../store/authStore';
import { api } from '../../api/client';
import './turmas-professor-desktop.css';
import './professor-dashboard-desktop.css';

type TurmaRow = TurmaDetalheData & {
  diasLecionados: Set<string>;
  materias: Set<string>;
};

export interface MinhasTurmasDesktopProps {
  turmasComContagem: TurmaRow[];
  isLoading: boolean;
  pendentesChamada: number;
  diaSemana?: string | null;
  horariosPorDia: ReturnType<typeof useMinhasTurmasProfessor>['horariosPorDia'];
  contagemAlunos: Record<string, number>;
  initialTurmaId?: string | null;
}

export function MinhasTurmasDesktop({
  turmasComContagem,
  isLoading,
  pendentesChamada,
  diaSemana,
  horariosPorDia,
  contagemAlunos,
  initialTurmaId,
}: MinhasTurmasDesktopProps) {
  const navigate = useNavigate();
  const escolaId = useAuthStore((s) => s.escolaId);
  const [busca, setBusca] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(initialTurmaId ?? null);

  const filtered = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return turmasComContagem;
    return turmasComContagem.filter((t) => {
      const materias = [...t.materias].join(' ').toLowerCase();
      return (
        t.nome.toLowerCase().includes(q) ||
        t.serie?.toLowerCase().includes(q) ||
        materias.includes(q) ||
        (TURNO_LABEL[t.turno || ''] || t.turno || '').toLowerCase().includes(q)
      );
    });
  }, [turmasComContagem, busca]);

  useEffect(() => {
    if (filtered.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !filtered.some((t) => t.id === selectedId)) {
      setSelectedId(filtered[0].id);
    }
  }, [filtered, selectedId]);

  const selected = filtered.find((t) => t.id === selectedId) ?? null;
  const grade = selected ? buildGradeTurma(selected.id, horariosPorDia) : [];

  const totalAlunos = turmasComContagem.reduce(
    (s, t) => s + (contagemAlunos[t.id] ?? t.totalAlunos),
    0,
  );
  const aulasHojeTotal = turmasComContagem.reduce((s, t) => s + t.aulasHoje, 0);
  const aulasSemanaTotal = turmasComContagem.reduce((s, t) => s + t.aulasSemana, 0);

  const [alunosTurma, setAlunosTurma] = useState<{ id: string; nome: string }[]>([]);
  const [loadingAlunos, setLoadingAlunos] = useState(false);

  useEffect(() => {
    if (!selectedId || !escolaId) {
      setAlunosTurma([]);
      return;
    }
    let cancelled = false;
    setLoadingAlunos(true);
    api
      .get<{ alunos?: { id: string; nome: string }[] }>(
        `/escolas/${escolaId}/chamadas/turma/${selectedId}/alunos`,
      )
      .then(({ data }) => {
        if (!cancelled) setAlunosTurma(data.alunos ?? []);
      })
      .catch(() => {
        if (!cancelled) setAlunosTurma([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingAlunos(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId, escolaId]);

  return (
    <div className="admin-page text-[13px] leading-snug dash-desk-page tur-desk-page">
      <div className="dash-desk-root tur-desk-root">
        <div className="dash-desk-top tur-desk-top">
          <div className="dash-desk-greeting">
            <h1 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>
              Minhas turmas
            </h1>
            <p className="tur-desk-subtitle">
              {turmasComContagem.length} turma{turmasComContagem.length !== 1 ? 's' : ''} na sua grade · somente consulta
            </p>
          </div>
          <GraduationCap size={18} style={{ color: '#2563eb', opacity: 0.85 }} aria-hidden />
        </div>

        <div className="tur-desk-metrics">
          <div className="dash-desk-kpi">
            <div className="dash-desk-kpi-icon" style={{ background: '#eff6ff', color: '#2563eb' }}>
              <GraduationCap size={15} />
            </div>
            <div>
              <div className="dash-desk-kpi-val">{turmasComContagem.length}</div>
              <div className="dash-desk-kpi-lbl">Turmas</div>
            </div>
          </div>
          <div className="dash-desk-kpi">
            <div className="dash-desk-kpi-icon" style={{ background: '#f0fdf4', color: '#059669' }}>
              <Users size={15} />
            </div>
            <div>
              <div className="dash-desk-kpi-val">{totalAlunos}</div>
              <div className="dash-desk-kpi-lbl">Alunos</div>
            </div>
          </div>
          <div className="dash-desk-kpi">
            <div className="dash-desk-kpi-icon" style={{ background: '#ede9fe', color: '#7c3aed' }}>
              <Calendar size={15} />
            </div>
            <div>
              <div className="dash-desk-kpi-val">{aulasSemanaTotal}</div>
              <div className="dash-desk-kpi-lbl">Aulas/semana</div>
            </div>
          </div>
          <div className="dash-desk-kpi">
            <div
              className="dash-desk-kpi-icon"
              style={{
                background: pendentesChamada ? '#fffbeb' : '#f8fafc',
                color: pendentesChamada ? '#d97706' : '#94a3b8',
              }}
            >
              <Clock size={15} />
            </div>
            <div>
              <div className="dash-desk-kpi-val">{aulasHojeTotal}</div>
              <div className="dash-desk-kpi-lbl">Aulas hoje</div>
            </div>
          </div>
        </div>

        <div className="tur-desk-notice">
          <Info size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            Turmas da <strong>sua grade de horários</strong>. Alunos via{' '}
            <code style={{ fontSize: '0.65rem' }}>chamadas/turma/…/alunos</code> (sem lista da escola inteira).
          </span>
        </div>

        {pendentesChamada > 0 && (
          <div className="tur-desk-alert">
            <AlertTriangle size={14} />
            {pendentesChamada} turma{pendentesChamada > 1 ? 's' : ''} com chamada pendente hoje
          </div>
        )}

        <div className="tur-desk-toolbar">
          <div className="tur-desk-search-wrap">
            <Search size={14} />
            <input
              type="search"
              className="tur-desk-search"
              placeholder="Buscar turma, série, matéria…"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
          <span className="tur-desk-count">{filtered.length} turma(s)</span>
        </div>

        <div className="tur-desk-body">
          <div className="tur-desk-list-card">
            <div className="tur-desk-list-scroll">
              {isLoading ? (
                <p className="tur-desk-loading">Carregando turmas…</p>
              ) : filtered.length === 0 ? (
                <p className="tur-desk-loading">
                  Nenhuma turma na sua grade. Peça à secretaria para vincular suas aulas.
                </p>
              ) : (
                filtered.map((t) => {
                  const pendente = t.aulasHoje > 0 && !t.chamadaHojeFeita;
                  const qtd = contagemAlunos[t.id];
                  const materiasStr = [...t.materias].join(', ');
                  const active = selectedId === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      className={[
                        'tur-desk-list-item',
                        active ? 'tur-desk-list-item--active' : '',
                        pendente ? 'tur-desk-list-item--pend' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => setSelectedId(t.id)}
                    >
                      <div className="tur-desk-list-icon" aria-hidden>
                        <GraduationCap size={16} />
                      </div>
                      <div className="tur-desk-list-main">
                        <p className="tur-desk-list-title">
                          {t.turno
                            ? `${t.nome} · ${TURNO_LABEL[t.turno] || t.turno}`
                            : t.nome}
                        </p>
                        <div className="tur-desk-list-meta">
                          <span>
                            <Users size={10} />
                            {qtd !== undefined ? qtd : '…'} al.
                          </span>
                          <span>
                            <BookOpen size={10} />
                            {t.materias.size} mat.
                          </span>
                          {t.aulasHoje > 0 && (
                            <span>
                              <Clock size={10} />
                              {t.aulasHoje} hoje
                            </span>
                          )}
                          {t.sala && (
                            <span>
                              <MapPin size={10} />
                              {t.sala}
                            </span>
                          )}
                        </div>
                        {materiasStr && (
                          <p className="tur-desk-list-materias">Leciona: {materiasStr}</p>
                        )}
                      </div>
                      <div className="tur-desk-list-aside">
                        {t.aulasHoje > 0 &&
                          (t.chamadaHojeFeita ? (
                            <span className="tur-desk-badge tur-desk-badge--ok">
                              <CheckCircle2 size={10} /> OK
                            </span>
                          ) : (
                            <span className="tur-desk-badge tur-desk-badge--pend">Pend.</span>
                          ))}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div className="tur-desk-detail-card">
            <div className="tur-desk-detail-scroll">
              {selected ? (
                <ProfessorTurmaPainel
                  turma={selected}
                  grade={grade}
                  diaSemanaHoje={diaSemana}
                  alunos={alunosTurma}
                  loadingAlunos={loadingAlunos}
                  variant="panel"
                  onAlunoClick={(id) => navigate('/professor/alunos', { state: { fichaId: id } })}
                />
              ) : (
                <div className="tur-desk-detail-empty">
                  Selecione uma turma para ver alunos, grade semanal e status da chamada.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
